import { gql } from '@apollo/client'
import { Col, Container, Row, Separator } from '@farol-ds/react'
import { Footer } from '@jusbrasil/footer'
import { Topbar, TopbarCurrentNavKey } from '@jusbrasil/topbar'
import { BifrostModalTrigger } from '@jusbrasil-web/shared-bifrost'
import { ContextMenuItem } from '@jusbrasil-web/shared-farol-context-menu'
import { useGTM } from '@jusbrasil-web/shared-gtm'
import classNames from 'classnames'
import { ReactElement, ReactNode, RefObject, useEffect } from 'react'

import layoutStyles from '../../layout.module.scss'
import { MetricsContext, MetricsProperties } from '../../metrics'
import printStyles from '../../print.module.scss'
import { DetailsModal } from '../details-modal'
import { DocContextMenu } from '../doc-context-menu'
import { DocPopover, DocPopoverElement } from '../doc-popover'
import { DocPrintable } from '../doc-printable'
import { Docview_DocumentFragment, Docview_ViewerFragment } from './__generated__/docview.graphql'

type LayoutProps = {
  header: ReactNode
  asideContent: ReactNode
  mainContent: ReactNode
  relatedContent?: ReactNode
  asidePosition?: 'left' | 'right'
}

export type DocviewProps = {
  document: Docview_DocumentFragment
  viewer: Docview_ViewerFragment | null
  metricsProperties: MetricsProperties
  canCopyContent: boolean
  disableTouchFeatures: boolean
  handleCopy?: (event: React.ClipboardEvent<HTMLDivElement>) => void
  onShowMoreDetailsModalClick?: () => void
  dispatcherRef: RefObject<HTMLDivElement>
  contentRef: RefObject<HTMLDivElement>
  topbarCurrentNavKey?: TopbarCurrentNavKey
  hasLawsuitAnalysis?: boolean
  docPopoverRef?: RefObject<DocPopoverElement>
  afterTopbar?: ReactNode
  endContent?: ReactNode
  metaTags?: ReactNode
  showModalHeader?: boolean
  isSegmentedPublication?: boolean
  extraContentMenuItems?: ReactElement<typeof ContextMenuItem>[]
} & (
  | ({ children: ReactNode } & { [P in keyof LayoutProps]?: never })
  | ({ children?: never } & LayoutProps)
)

export function Docview(props: DocviewProps) {
  const {
    document,
    viewer,
    metricsProperties,
    afterTopbar,
    header,
    asideContent,
    mainContent,
    relatedContent,
    endContent,
    canCopyContent,
    disableTouchFeatures,
    handleCopy,
    dispatcherRef,
    contentRef,
    topbarCurrentNavKey,
    hasLawsuitAnalysis,
    docPopoverRef,
    isSegmentedPublication,
    onShowMoreDetailsModalClick,
    metaTags,
    extraContentMenuItems = [],
    showModalHeader = true,
    asidePosition = 'right',
  } = props

  const { children } = props
  const gtm = useGTM()
  useEffect(() => {
    gtm.push({ ga4_resource_id: document.boxResourceId })
  }, [document.boxResourceId, gtm])

  const isLeftAside = asidePosition === 'left'

  const sidebarContent = (
    <Col xs={12} md={4}>
      <aside
        className={classNames(layoutStyles.sidebarWrapper, {
          [layoutStyles.sidebarWrapperLeft]: isLeftAside,
        })}
      >
        {asideContent}
      </aside>
    </Col>
  )

  const renderChildren = () => {
    if (children) {
      return (
        <div ref={dispatcherRef} onCopy={handleCopy}>
          {children}
        </div>
      )
    }

    return (
      <Container
        ref={dispatcherRef}
        className={classNames(layoutStyles.container, layoutStyles.anchorWrapper)}
        onCopy={handleCopy}
      >
        <Row>
          {isLeftAside && sidebarContent}
          <Col xs={12} md={8} asChild>
            <main
              className={classNames(layoutStyles.main, {
                [layoutStyles.mainWithLeftAside]: isLeftAside,
              })}
            >
              <div className={layoutStyles.mainContent}>
                {header}

                {!isSegmentedPublication && (
                  <DetailsModal
                    onShowMoreDetailsModalClick={onShowMoreDetailsModalClick}
                    showModalHeader={showModalHeader}
                  >
                    {asideContent}
                  </DetailsModal>
                )}

                {mainContent}

                {isSegmentedPublication && (
                  <div className={layoutStyles.detailsModalWrapper}>
                    <DetailsModal
                      onShowMoreDetailsModalClick={onShowMoreDetailsModalClick}
                      showModalHeader={showModalHeader}
                    >
                      {asideContent}
                    </DetailsModal>
                  </div>
                )}
                {relatedContent && (
                  <Separator
                    className={classNames(layoutStyles.separator, {
                      [layoutStyles.separatorHidden]: isSegmentedPublication,
                    })}
                  />
                )}
                {relatedContent}
              </div>
            </main>
          </Col>
          {!isLeftAside && sidebarContent}
        </Row>
      </Container>
    )
  }

  return (
    <MetricsContext.Provider value={metricsProperties}>
      <DocContextMenu extraContextMenuItems={extraContentMenuItems} />
      {metaTags}

      <Topbar
        currentNavKey={topbarCurrentNavKey}
        className={printStyles.unprintable}
        headerProps={{ disableCta: isSegmentedPublication }}
        variant={isSegmentedPublication || hasLawsuitAnalysis ? 'slim' : 'default'}
      />

      {afterTopbar}

      <DocPrintable viewer={viewer} document={document} canCopy={canCopyContent}>
        {renderChildren()}
      </DocPrintable>

      <Footer className={printStyles.unprintable} />
      <BifrostModalTrigger viewer={viewer} />
      <DocPopover
        contentRef={contentRef}
        disableTouchFeatures={disableTouchFeatures}
        ref={docPopoverRef}
        hasLRSubscription={!!viewer?.hasLegalResearch}
      />

      {endContent}
    </MetricsContext.Provider>
  )
}

Docview.fragments = {
  document: gql`
    fragment Docview_document on Document {
      boxResourceId
      ...DocPrintable_document
    }

    ${DocPrintable.fragments.document}
  `,

  viewer: gql`
    fragment Docview_viewer on Profile {
      ...BifrostModalTrigger_viewer
      ...DocPrintable_viewer
      hasLegalResearch
    }

    ${BifrostModalTrigger.fragments.viewer}
    ${DocPrintable.fragments.viewer}
  `,
}
